import React from 'react';
import {
  Box,
  Typography,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Update as UpdateIcon,
  Warning as WarningIcon,
  TableChart as TableIcon
} from '@mui/icons-material';

interface CompareSummaryProps {
  summary: {
    totalRecords: number;
    newRecords: number;
    existingRecords: number;
    conflicts: number;
  };
  onFilterChange?: (filterType: 'current' | 'incoming' | 'new' | 'conflicted') => void;
  activeFilter?: 'current' | 'incoming' | 'new' | 'conflicted';
}

const CompareSummary: React.FC<CompareSummaryProps> = ({ summary, onFilterChange, activeFilter }) => {
  const theme = useTheme();

  const summaryItems = [
    {
      title: 'Incoming Records',
      value: summary.totalRecords, // Total records in incoming data
      icon: <TableIcon />,
      color: '#7b1fa2',
      bgColor: 'rgba(123, 31, 162, 0.08)',
      textColor: '#7b1fa2',
      filterType: 'incoming' as const
    },
    {
      title: 'Current Records',
      value: summary.existingRecords,
      icon: <UpdateIcon />,
      color: '#1976d2',
      bgColor: 'rgba(25, 118, 210, 0.08)',
      textColor: '#1976d2',
      filterType: 'current' as const
    },
    {
      title: 'New Records',
      value: summary.newRecords,
      icon: <AddIcon />,
      color: '#2e7d32',
      bgColor: 'rgba(46, 125, 50, 0.08)',
      textColor: '#2e7d32',
      filterType: 'new' as const
    },
    {
      title: 'Conflicted Records',
      value: summary.conflicts,
      icon: <WarningIcon />,
      color: '#ed6c02',
      bgColor: 'rgba(237, 108, 2, 0.08)',
      textColor: '#ed6c02',
      filterType: 'conflicted' as const
    }
  ];

  // Filter out items with 0 values - only show filters that have data
  const filteredSummaryItems = summaryItems.filter(item => item.value > 0);

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 1.5, 
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'flex-start'
    }}>
      {filteredSummaryItems.map((item, index) => (
        <Box
          key={index}
          onClick={() => onFilterChange?.(item.filterType)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: activeFilter === item.filterType ? `${item.color}15` : item.bgColor,
            color: item.textColor,
            borderRadius: 2,
            border: `1px solid ${activeFilter === item.filterType ? item.color : `${item.color}30`}`,
            transition: 'all 0.15s ease',
            cursor: onFilterChange ? 'pointer' : 'default',
            minWidth: 'fit-content',
            '&:hover': {
              backgroundColor: `${item.color}12`,
              borderColor: item.color,
              transform: 'translateY(-1px)'
            }
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontSize: '1.1rem'
          }}>
            {item.icon}
          </Box>
          <Typography variant="body2" sx={{ 
            fontWeight: 600,
            fontSize: '0.85rem',
            whiteSpace: 'nowrap'
          }}>
            {item.value} {item.title}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default CompareSummary;
